import { createTheme } from "@mui/material/styles";

export const landingTheme = createTheme({
    palette: {
        mode: "dark",
        primary: { main: "#e8c97e", dark: "#c9a84c", light: "#f0dba8" },
        secondary: { main: "#4ecdc4", dark: "#2bada5", light: "#7fddd8" },
        background: { default: "#0a0c0f", paper: "#111418" },
        text: { primary: "#f0ede8", secondary: "#8a8f98" },
        divider: "rgba(255,255,255,0.07)",
    },
    typography: {
        fontFamily: '"DM Serif Display", "Playfair Display", Georgia, serif',
        h1: { fontWeight: 400, letterSpacing: "-0.02em", lineHeight: 1.05 },
        h2: { fontWeight: 400, letterSpacing: "-0.015em", lineHeight: 1.12 },
        h3: { fontWeight: 400, letterSpacing: "-0.01em" },
        h4: { fontWeight: 400 },
        h5: { fontWeight: 400 },
        body1: { fontFamily: '"DM Sans", "Helvetica Neue", sans-serif', lineHeight: 1.7 },
        body2: { fontFamily: '"DM Sans", "Helvetica Neue", sans-serif', lineHeight: 1.65 },
        button: { fontFamily: '"DM Sans", "Helvetica Neue", sans-serif', textTransform: "none", fontWeight: 600, letterSpacing: 0.2 },
        caption: { fontFamily: '"DM Sans", "Helvetica Neue", sans-serif' },
        overline: { fontFamily: '"DM Sans", "Helvetica Neue", sans-serif', fontWeight: 600 },
        subtitle1: { fontFamily: '"DM Sans", "Helvetica Neue", sans-serif', fontWeight: 600 },
        subtitle2: { fontFamily: '"DM Sans", "Helvetica Neue", sans-serif', fontWeight: 600 },
    },
    shape: { borderRadius: 4 },
    components: {
        MuiButton: {
            styleOverrides: {
                root: { borderRadius: 3 },
                containedPrimary: {
                    color: "#0a0c0f",
                    background: "linear-gradient(135deg, #f0dba8 0%, #e8c97e 100%)",
                    "&:hover": { background: "linear-gradient(135deg, #f5e4bb 0%, #efd48c 100%)" },
                },
            },
        },
        MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
        MuiChip: { styleOverrides: { root: { borderRadius: 3 } } },
    },
});

export const TOP_NAV = [
    { label: "Get Started", id: "getting-started" },
    { label: "Admin", id: "admin-module" },
    { label: "Teachers", id: "teacher-management" },
    { label: "Students", id: "student-management" },
    { label: "Finance", id: "fee-management" },
    { label: "Hostel", id: "hostel-management" },
];

export const TOC = [
    { id: "getting-started", label: "Getting Started" },
    { id: "admin-module", label: "Admin Dashboard & Profile" },
    { id: "class-subject-management", label: "Classes & Subjects" },
    { id: "student-management", label: "Student Management" },
    { id: "teacher-management", label: "Teacher & Staff Management" },
    { id: "attendance-marks-review", label: "Attendance & Marks Review" },
    { id: "scheme-of-work-approvals", label: "Scheme of Work & Approvals" },
    { id: "fee-management", label: "Fee Management" },
    { id: "expense-budget-management", label: "Expense & Budget Management" },
    { id: "hostel-management", label: "Hostel & Boarding Management" },
    { id: "timetable-calendar", label: "Timetable & Calendar" },
    { id: "notices-communication", label: "Notices & Announcements" },
    { id: "teacher-portal", label: "Teacher Portal" },
    { id: "student-portal", label: "Student Portal" },
    { id: "reports-analytics", label: "Reports & Analytics" },
];

export const SECTIONS = [
    {
        id: "getting-started",
        title: "Getting Started",
        description: "Set up your school on the platform in under 10 minutes",
        steps: [
            'Open the platform and click "Register your school" on the homepage',
            "Fill in your full name, school name, school type (Primary / Secondary / University / Combined), and residence offering (Day only, Boarding only, or Day & Boarding)",
            "Enter your email address and create a strong password",
            'Click "Create school account" — you will be redirected to your Admin dashboard',
            "On first login, complete your school profile: go to Admin → My Profile and fill in school address, phone, motto, and logo",
            "Create your first class: go to Classes → Add Class, enter the class name and save",
            "Add subjects to that class: go to Subjects → Add Subject",
            "Add your first teacher: go to Teachers → Add Teacher",
            "Add your first student: go to Students → Add Student, assign them to the class",
        ],
        tips: "Your school type determines the term structure and grading system. Primary uses 3 terms, Secondary uses 3 terms with MSCE grading, University uses semesters with GPA. You can change this later in your profile.",
    },
    {
        id: "admin-module",
        title: "Admin Dashboard & Profile",
        description: "Your central command panel — overview of your entire school at a glance",
        steps: [
            "After login you land on the Admin Dashboard — it shows live counts of students, teachers, classes, and recent activity",
            'The left sidebar organises all modules into groups: Academics, Students, Finance, Calendar, and Settings',
            'Click "My Profile" in the sidebar to update your school details — name, address, phone, email, logo, school motto, and boarding settings',
            'The "School Page" module lets you build a public-facing page for your school visible to parents and the community',
            "Use the top search bar to quickly jump to any student, teacher or class",
            "Notices posted from Admin are visible to all teachers and students in their portals",
        ],
        dashboardCards: [
            { title: "Total Students", description: "All enrolled students across all classes" },
            { title: "Total Teachers", description: "All active teaching staff" },
            { title: "Total Classes", description: "Number of class groups" },
            { title: "Fee Collection", description: "Total payments received this term" },
            { title: "Recent Activity", description: "Latest registrations, payments, and submissions" },
        ],
        tips: "Bookmark /Admin/dashboard for quick access. The dashboard auto-refreshes every time you navigate back to it.",
    },
    {
        id: "class-subject-management",
        title: "Classes & Subjects",
        description: "Organise your school into classes and assign subjects before adding students or teachers",
        steps: [
            "Go to Classes → Add Class in the sidebar",
            'Enter the class name (e.g. "Standard 1", "Form 3", "Year 1 Science") and optionally set a student capacity',
            "The class education level is automatically inferred from your school type",
            "To add subjects: go to Subjects → Add Subject, select the class, enter the subject name and number of sessions per week",
            'Mark a subject as "Elective" if it is optional for students',
            'Mark a subject as "MIE Registered" if it appears on the national exam (PSLCE/MSCE)',
            "To view all subjects in a class: go to Classes → click a class name → Subjects tab",
            "To delete a class: open the class and click the delete button — this also removes all students and subjects in that class",
        ],
        tips: "Create all your classes and subjects before adding teachers and students. Teachers are assigned to specific subjects, and students are enrolled in a class which gives them access to all that class's subjects.",
    },
    {
        id: "student-management",
        title: "Student Management",
        description: "Enrol, manage, and track every student from admission to results",
        steps: [
            "Go to Students → Add Student",
            "Fill in: full name, roll number (unique per class), class, gender, date of birth, guardian name, guardian phone, and guardian email",
            "Set admission date and admission status (Active, Suspended, Graduated, Transferred)",
            "Upload a student photo (optional, JPEG/PNG, max 5MB)",
            "For boarding schools: select Residence Type — Day or Boarding. If Boarding, assign a hostel room and bed label",
            "Set a login password for the student — they use their roll number and name to log in",
            "After saving, fee records are automatically created based on the active fee structure for that class",
            "To view a student: go to Students → click any student name to see full profile, results, attendance, and fee records",
            "To update student details: open the student profile and click Edit",
            "To delete a student: open the student and click Delete — this removes all their records permanently",
        ],
        tips: "Roll numbers must be unique within a class. Use a consistent numbering system like 001, 002, 003. The system will warn you if a roll number already exists in that class.",
    },
    {
        id: "teacher-management",
        title: "Teacher & Staff Management",
        description: "Add teaching staff, assign subjects and classes, and manage HR records",
        steps: [
            "Go to Teachers → Add Teacher",
            "Enter: full name, email address, and a temporary password",
            "Assign subjects for teaching; assign a form teacher on each class (Add class or class details) for enrolment and report cards",
            "Fill in optional HR details: phone number, qualification, years of experience, employment date, and salary",
            "The teacher receives their email and password to log into the Teacher Portal",
            "To assign additional subjects: open the teacher profile → Subjects tab → Add Subject",
            "To view a teacher's activity: open their profile to see classes taught, submissions, leave history, and salary records",
            'Staff roles: "Teacher" has full academic access. "Bursar" manages fees and expenses only. "Staff" has limited read access',
            "To record teacher attendance: go to Teachers → select a teacher → Attendance tab",
            "Salary payments are recorded under Finance → Teacher Salaries",
        ],
        tips: "Each teacher logs in with their email and password. They can update their own profile photo, bio, and contact details from their Teacher Portal without needing admin access.",
    },
    {
        id: "attendance-marks-review",
        title: "Attendance & Marks Review",
        description:
            "Teachers submit marks and attendance for admin approval — ensuring accuracy before results are finalised",
        workflow: [
            "Teacher enters marks/attendance",
            "Teacher submits for review",
            "Admin reviews submission",
            "Admin approves or rejects",
            "Records are saved officially",
        ],
        stepsTitle: "Steps for Admin",
        steps: [
            "Go to Admin → Marks Review (Exams → Review Submissions in sidebar)",
            "You will see a list of pending submissions from teachers — each shows the class, subject, teacher name, and submission date",
            "Click a submission to see the full list of marks or attendance records entered by the teacher",
            'Review each entry — if correct click "Approve All"',
            'If there are errors click "Reject" and add a rejection note — the teacher will see this and can resubmit',
            "Approved marks become official results visible on student report cards",
            "Go to Admin → Attendance Review to approve attendance submissions the same way",
            "Go to Admin → Scheme Approvals to review and approve teacher schemes of work",
        ],
        tips: "Only approved submissions count toward report cards and results. Rejected submissions are sent back to the teacher with your note. Teachers cannot change approved records.",
    },
    {
        id: "scheme-of-work-approvals",
        title: "Scheme of Work & Approvals",
        description:
            "MIE-aligned two-week scheme blocks with record of work. Teachers draft schemes; directors sign off before classroom use.",
        steps: [
            "Teachers create scheme blocks per subject with topics, objectives, and resources.",
            "Attach record-of-work entries as lessons are delivered.",
            "Submit completed schemes for director approval.",
            "Review scheme content in the admin approval queue.",
            "Approve or return schemes with feedback for revision.",
        ],
        tips: "Schemes follow Northern Education Division MIE format — keep blocks aligned to your term calendar.",
    },
    {
        id: "fee-management",
        title: "Fee Management",
        description: "Create fee structures, collect payments, issue receipts, and track defaulters",
        steps: [
            "Go to Finance → Fee Structures → Add Fee Structure",
            "Set: class, academic year, term, fee name, and total amount. Add individual fee items (tuition, boarding, PTA, etc.)",
            "Activate the structure — this automatically creates fee records for every student in that class",
            'To record a payment: go to Finance → Fee Collection → find the student → click "Record Payment"',
            "Enter the amount paid and payment method — the system generates a receipt number automatically (e.g. REC-2026-SCHOOL-00001)",
            "The student's fee balance updates instantly",
            'To apply a fee waiver: open the fee record → click "Apply Waiver" → enter the waiver amount and reason',
            'To void a payment: open the fee record → find the payment → click "Void" — this reverses the payment',
            "Finance → Fee Defaulters shows all students with outstanding balances, filterable by class and term",
            "Finance → Fee Reports shows collection summaries, class breakdowns, and audit logs",
        ],
        tips: "Fee structures are linked to classes and terms. If you add a new student after creating a fee structure, their fee record is created automatically. You can also manually assign fees to a specific student from their profile.",
    },
    {
        id: "expense-budget-management",
        title: "Expense & Budget Management",
        description: "Track every school expenditure, set budgets, and generate expense reports",
        steps: [
            "Go to Finance → Expense Categories — create categories first (e.g. Salaries, Utilities, Stationery, Maintenance)",
            "Go to Finance → Log Expense → Add Expense",
            "Fill in: expense title, category, amount, date, payment method, and attach a receipt or note",
            'Submit the expense — it appears in the log as "Pending" until approved',
            "Admin approves or rejects expenses from the Expense Log",
            "Approved expenses are automatically deducted from the relevant budget",
            "Go to Finance → Budgets to set a budget for each expense category per term or year",
            "Finance → Expense Summary shows total spend by category, monthly trends, and budget vs actual comparisons",
            "Export expense reports as a summary for auditing or management meetings",
        ],
        tips: "Set budgets before the term begins. The system will highlight categories where spending is approaching or exceeding the budget. Bursars can log expenses but only admins can approve them.",
    },
    {
        id: "hostel-management",
        title: "Hostel & Boarding Management",
        description: "Manage boarding facilities, room allocations, and boarder records",
        note: 'This module only appears if you selected "Boarding" or "Day & Boarding" when registering your school.',
        steps: [
            'Go to Admin → Hostels → Add Hostel — enter hostel name, gender (Boys/Girls/Mixed), and description',
            'Inside the hostel, add rooms: click "Add Room" — enter room number, capacity, and room type (Dormitory, Private, Semi-private)',
            'To assign a student to a hostel room: go to Students → open the student → Edit → set Residence Type to "Boarding", select hostel room, and enter bed label (e.g. Bed A, Bed 3)',
            "Alternatively assign from Hostels → Boarders → assign room to any boarding student",
            "Admin → Hostels → Boarders shows all boarding students with their room and bed assignments",
            "Room capacity is enforced — you cannot assign more students than the room capacity",
            "Hostel overview shows occupancy rates per hostel and per room",
        ],
        tips: "Boarding fees are automatically included in fee structures for boarding students. Make sure the student's Residence Type is set to Boarding so the correct fee structure applies.",
    },
    {
        id: "timetable-calendar",
        title: "Timetable & Calendar",
        description: "Schedule classes, set term dates, exam periods, and school events",
        steps: [
            "Go to Calendar → School Calendar to set term start and end dates, holiday periods, and exam weeks",
            "Go to Timetable → Class Timetable to build the weekly lesson schedule for each class",
            "Assign each period: select the day, time slot, subject, and teacher",
            "Students and teachers can view their timetable from their portals",
            "Add school events (sports day, parent meetings, etc.) to the shared calendar",
            "The calendar is visible to all staff and students after login",
        ],
    },
    {
        id: "notices-communication",
        title: "Notices & Announcements",
        description: "Send school-wide notices to all staff and students instantly",
        steps: [
            "Go to Notices → Add Notice",
            "Enter a title, full notice text, and date",
            "Set the audience: All, Teachers only, Students only, or a specific class",
            "Save — the notice appears immediately in teacher and student portals",
            "To edit or delete a notice: go to Notices → All Notices → find the notice and click edit or delete",
            'Students see notices in their portal under "Notices"',
            'Teachers see notices in their portal under "Announcements"',
        ],
        tips: "Use notices for term reminders, exam schedules, fee payment deadlines, and event announcements. Notices are date-stamped and shown in reverse chronological order.",
    },
    {
        id: "teacher-portal",
        title: "Teacher Portal",
        description: "Everything a teacher needs — marks entry, attendance, schemes, lesson plans, and assignments",
        steps: [
            "Teachers log in at /login with their email and password set by the admin",
            "The Teacher Dashboard shows their assigned class, subjects, pending submissions, and recent activity",
            "Record Marks: go to My Class → Record Marks → select the subject and exam type → enter marks for each student → click Submit for Review",
            "Record Attendance: go to My Class → Record Attendance → mark each student Present, Absent, or Late → Submit for Review",
            "Scheme of Work: go to Scheme of Work → create a new scheme for a subject and term → add weekly topics and objectives → submit for admin approval",
            "Lesson Plans: go to Lesson Plans → create a daily lesson plan with objectives, resources, and methodology",
            "Assignments: go to Assignments → create an assignment with title, description, due date, and class",
            "Resources: upload teaching materials (PDFs, Word docs, presentations) under Class Resources for students to access",
            "Leave Application: go to Apply for Leave → fill in leave type, dates, and reason → submit to admin",
            "Teacher Profile: update personal details, photo, qualifications, and contact information",
        ],
        tips: "Submitted marks and attendance go to Admin for review before they become official. You will be notified if your submission is rejected — open it to see the rejection note and correct the errors before resubmitting.",
    },
    {
        id: "student-portal",
        title: "Student Portal",
        description: "Students access their results, attendance, fees, resources, and notices after login",
        steps: [
            "Students log in at /login — select Student, enter their Roll Number, Full Name, and password",
            "The Student Dashboard shows a summary of attendance, results, fee balance, and recent notices",
            "Results: go to My Results to view all exam marks by subject and term. Download or print the report card",
            "Attendance: go to My Attendance to see attendance percentage per subject and dates of absences",
            "Fees: go to My Fees to see all fee records, amounts due, amounts paid, balance, and payment receipts",
            "Subjects: go to My Subjects to see all subjects for their class with teacher names and session counts",
            "Resources: go to Class Resources to download teaching materials uploaded by their teachers",
            "Notices: go to Notices to read all school announcements",
            "Profile: students can view their own profile — name, class, guardian contact, and photo",
            "Complains: students can submit a complaint or concern through the portal which goes to Admin",
        ],
        tips: "Students cannot edit their own marks, attendance, or fee records. These are managed by teachers and admin. If a student believes a record is wrong they should contact their teacher or admin.",
    },
    {
        id: "reports-analytics",
        title: "Reports & Analytics",
        description: "Generate report cards, class rankings, fee summaries, and financial reports",
        steps: [
            "Student Report Card: go to Exams → Results Overview → select a class → click a student → Print Report Card. The report card shows all subjects, marks, grade, position in class, and attendance summary",
            "Class Results: go to Exams → Results Overview → select a class to see the full class leaderboard with rankings",
            "University Transcript: for university schools, go to Exams → Student Transcript for a full academic transcript with GPA",
            "Fee Reports: go to Finance → Fee Reports for collection totals, class breakdowns, payment history, and audit log",
            "Expense Summary: go to Finance → Expense Summary for budget vs actual, category breakdown, and monthly trend charts",
            "All reports are printable — use the Print button on any report page",
        ],
    },
];
export const DOC_BASE = "/docs";

export function docPath(sectionId) {
    return `${DOC_BASE}/${sectionId}`;
}

export function getSectionById(id) {
    return SECTIONS.find((s) => s.id === id);
}

export function getSectionIndex(id) {
    return SECTIONS.findIndex((s) => s.id === id);
}

export function isValidSectionId(id) {
    return getSectionIndex(id) >= 0;
}
